<template>
  <div class="relative">

        <div class="p-6 space-y-6">
          <q-banner
            v-if="!isNative"
            class="bg-orange-100 text-orange-800 border border-orange-200 rounded-lg"
            rounded
            inline-actions
          >
            <div class="flex items-center gap-2">
              <q-icon name="warning" color="orange-7" />
              <span>当前为 Web 环境，原生能力不可用</span>
            </div>
          </q-banner>

          <div class="grid grid-cols-1 gap-6">
            <div
              class="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 shadow border border-gray-200 dark:border-gray-600 space-y-4"
            >
              <div class="flex items-center gap-3">
                <div
                  class="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center"
                >
                  <q-icon name="fact_check" class="text-white text-xl" />
                </div>
                <div>
                  <h2 class="text-lg font-bold text-gray-800 dark:text-gray-100">
                    获取车辆信息
                  </h2>
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    选择或输入 LiCarDiagManager 的 infoId
                  </p>
                </div>
              </div>

              <div class="space-y-4">
                <q-select
                  :options="infoIdOptions"
                  v-model="selectedInfoId"
                  emit-value
                  map-options
                  use-input
                  fill-input
                  hide-dropdown-icon
                  input-debounce="0"
                  label="选择或直接输入 infoId"
                  dense
                  outlined
                  bg-color="white"
                  class="w-full"
                />

                <button
                  @click="handleGetInfo"
                  :disabled="!selectedInfoId || loading"
                  class="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none"
                >
                  <div class="flex items-center justify-center gap-3">
                    <q-spinner-dots v-if="loading" class="text-white" />
                    <q-icon v-else name="cloud_download" class="text-xl" />
                    <span>{{ loading ? "读取中..." : "读取车辆信息" }}</span>
                  </div>
                </button>
              </div>
            </div>

            <div
              class="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-6 border border-gray-200 dark:border-gray-600 space-y-4"
            >
              <div class="flex items-center gap-2">
                <q-icon name="assignment" class="text-blue-500" />
                <span class="text-gray-800 dark:text-gray-200 font-semibold">
                  返回结果
                </span>
              </div>

              <div
                v-if="error"
                class="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700/50 rounded-lg p-4 text-red-700 dark:text-red-200"
              >
                {{ error }}
              </div>

              <div
                v-else
                class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-4"
              >
                <div class="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-2">
                  <span>infoId</span>
                  <span class="font-mono">{{ selectedInfoId || "--" }}</span>
                </div>
                <div class="text-xl font-bold text-gray-900 dark:text-gray-100 break-all">
                  {{ result || "暂无数据" }}
                </div>
              </div>
            </div>
          </div>
        </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useQuasar } from "quasar";
import { isNative } from "@/utils/capacitor";
import CarDiag from "@/utils/capacitor/carDiagPlugin";
import { VehicleInfoIds } from "@/utils/capacitor/carPlugin";

const $q = useQuasar();

const infoIdOptions = computed(() =>
  Object.entries(VehicleInfoIds).map(([key, value]) => ({
    label: `${key} (${value})`,
    value,
  }))
);

const selectedInfoId = ref(infoIdOptions.value[0]?.value || "vin_code");
const result = ref<string>("");
const loading = ref(false);
const error = ref<string>("");


const handleGetInfo = async () => {
  const infoId =
    typeof selectedInfoId.value === "string"
      ? selectedInfoId.value
      : (selectedInfoId.value as any)?.value;

  if (!infoId) {
    error.value = "信息ID不能为空";
    $q.notify({ type: "warning", message: error.value, position: "top" });
    return;
  }
  if (!isNative) {
    error.value = "仅在原生环境可用";
    $q.notify({ type: "warning", message: error.value, position: "top" });
    return;
  }

  loading.value = true;
  error.value = "";
  try {
    const res = await CarDiag.getVehicleInfo({ infoId });
    result.value = res?.value || "";
  } catch (err: any) {
    error.value = err?.message || "获取车辆信息失败";
    result.value = "";
    $q.notify({ type: "negative", message: error.value, position: "top" });
  } finally {
    loading.value = false;
  }
};
</script>

